import { ArrowLeft, RotateCcw, Sparkles } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Alert,
  AlertDescription,
  Badge,
  Button,
  Card,
  Item,
  Spinner,
} from "@opencloud/ui";
import { useState } from "react";
import { agentBrand } from "../brand.js";
import type { AgentAppView, AgentRelease } from "../types.js";
import { AppIcon } from "./AppIcon.js";
import { Evidence } from "./Evidence.js";

export function ReleaseHistory({
  app,
  releases,
  openReleaseId,
  busy,
  error,
  onBack,
  onOpen,
  onRevert,
}: {
  app: AgentAppView;
  releases: readonly AgentRelease[];
  openReleaseId: string | null;
  busy: boolean;
  error: string | null;
  onBack: () => void;
  onOpen: (releaseId: string | null) => void;
  onRevert: (release: AgentRelease) => void;
}) {
  const open = releases.find((release) => release.id === openReleaseId) ?? null;
  const [confirming, setConfirming] = useState<string | null>(null);
  return (
    <div
      className="a3-theme a3-history-shell"
      data-oc-theme={agentBrand().themeId}
      data-design-system="opencloud-ui"
    >
      <main className="a3-history">
        <header className="a3-history-header">
          <Button
            type="button"
            className="a3-action a3-action-quiet"
            variant="ghost"
            onClick={open ? () => onOpen(null) : onBack}
          >
            <ArrowLeft aria-hidden="true" />
            {open ? "All releases" : "Back to the conversation"}
          </Button>
          <div className="a3-secondary-app-identity">
            <AppIcon app={app} />
            <p className="a3-eyebrow">{app.name}</p>
          </div>
          <h1>{open ? "What changed" : "What’s been released"}</h1>
        </header>

        {error ? (
          <Alert variant="destructive" className="a3-error-banner">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}

        {open ? (
          <ReleaseDetail
            release={open}
            busy={busy}
            confirming={confirming === open.id}
            onAskRevert={() => setConfirming(open.id)}
            onCancelRevert={() => setConfirming(null)}
            onRevert={() => {
              setConfirming(null);
              onRevert(open);
            }}
          />
        ) : releases.length > 0 ? (
          <ReleaseList releases={releases} onOpen={onOpen} />
        ) : (
          <Card className="a3-history-empty">
            <h2>No releases yet</h2>
            <p>
              The first version appears here after the agent builds, checks, and
              publishes it.
            </p>
          </Card>
        )}
      </main>
    </div>
  );
}

function ReleaseList({
  releases,
  onOpen,
}: {
  releases: readonly AgentRelease[];
  onOpen: (releaseId: string) => void;
}) {
  return (
    <ul className="a3-release-list">
      {releases.map((release) => (
        <Item
          key={release.id}
          render={<li />}
          variant="default"
          className="a3-release-list-item"
          data-state={release.current ? "live" : release.state}
        >
          <Button
            type="button"
            className="a3-release-entry"
            variant="ghost"
            onClick={() => onOpen(release.id)}
          >
            <span className="a3-release-when">
              {relativeDay(release.activatedAt ?? release.createdAt) ? (
                <strong>
                  {relativeDay(release.activatedAt ?? release.createdAt)}
                </strong>
              ) : null}
              <span>
                {absoluteDate(release.activatedAt ?? release.createdAt)}
              </span>
            </span>
            <span className="a3-release-body">
              <strong>{release.summary}</strong>
              {release.kind === "revert" ? (
                <span className="a3-release-revert-of">
                  Returned to an earlier version
                </span>
              ) : null}
            </span>
            <span className="a3-release-meta">
              {release.automatic ? (
                <Badge className="a3-release-cause" tone="info" data-cause="automated">
                  <Sparkles aria-hidden="true" /> Fixed automatically
                </Badge>
              ) : null}
              {release.current ? (
                <Badge className="a3-release-live" tone="success">Live now</Badge>
              ) : null}
              {release.state === "failed" ? (
                <Badge className="a3-release-dead" tone="danger">Never went live</Badge>
              ) : null}
            </span>
          </Button>
        </Item>
      ))}
    </ul>
  );
}

function ReleaseDetail({
  release,
  busy,
  confirming,
  onAskRevert,
  onCancelRevert,
  onRevert,
}: {
  release: AgentRelease;
  busy: boolean;
  confirming: boolean;
  onAskRevert: () => void;
  onCancelRevert: () => void;
  onRevert: () => void;
}) {
  return (
    <Card className="a3-release-detail">
      <header>
        <h2>{release.summary}</h2>
        <p className="a3-release-detail-meta">
          {absoluteDate(release.activatedAt ?? release.createdAt)}
          {release.current ? " · Live now" : ""}
          {release.automatic ? " · Fixed automatically" : ""}
        </p>
      </header>

      {busy ? (
        <Alert className="a3-revert-status">
          <Spinner aria-label="Returning to the selected version" />
          <AlertDescription>
            {`Returning to this version. The current app remains available while ${agentBrand().productName} restores and verifies it.`}
          </AlertDescription>
        </Alert>
      ) : null}

      <section className="a3-change-description">
        <h3>What changed</h3>
        <div className="a3-change-group">
          <h4>Release</h4>
          <ul>
            <li>{release.summary}</li>
          </ul>
        </div>
        {release.capabilities.length > 0 ? (
          <div className="a3-change-group">
            <h4>Parts included</h4>
            <ul>
              {release.capabilities.map((capability) => (
                <li key={capability}>{capability}</li>
              ))}
            </ul>
          </div>
        ) : null}
        <p className="a3-preserved">
          Information already saved by the app remains in place.
        </p>
        {release.canRevert ? (
          <p className="a3-data-change">
            <strong>Returning to this version</strong> changes the app code. It
            does not reverse information already transformed by a later release.
          </p>
        ) : null}
      </section>

      {release.reviews.length > 0 || release.gates.length > 0 ? (
        <section className="a3-release-evidence-record">
          <h3>How this was checked</h3>
          <Evidence reviews={release.reviews} gates={release.gates} record />
        </section>
      ) : null}

      {release.current ? (
        <p className="a3-release-detail-note">
          This is the version your app is running now.
        </p>
      ) : release.canRevert && !busy ? (
        <Button
          type="button"
          className="a3-action a3-action-primary"
          variant="primary"
          onClick={onAskRevert}
        >
          <RotateCcw aria-hidden="true" /> Return to this version
        </Button>
      ) : null}

      <AlertDialog
        open={confirming}
        onOpenChange={(open) => {
          if (!open && !busy) onCancelRevert();
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Return the app to release {release.version}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {`${agentBrand().productName} creates a new activation from this release. Release history and saved information stay intact; later data transformations are not undone.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction disabled={busy} onClick={onRevert}>
              {busy ? <Spinner aria-label="Returning to this version" /> : null}
              {busy ? "Returning…" : "Return to this version"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

function absoluteDate(value: string): string {
  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(new Date(value));
}

function relativeDay(value: string): string | null {
  const date = new Date(value);
  const today = new Date();
  const start = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  ).getTime();
  const target = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
  ).getTime();
  const days = Math.round((start - target) / 86_400_000);
  return days === 0 ? "Today" : days === 1 ? "Yesterday" : null;
}
